import { DEFAULT_LLM_MODEL } from '../npc/config';
import { cloneFallbackProviders } from '../lib/llmConfig';
import { normalizeInlineSpriteColors } from '../lib/spriteColors';
import { defaultNpcs } from '../services/npcFallback';
import {
  DEFAULT_MOVEMENT_CONFIG,
  cloneMovementConfig,
  movementConfigFromWaypointPath,
  normalizeMovementConfig,
  toLegacyWaypointPath,
} from '../lib/npcMovement';
import type { NpcConfig, NpcDefinition } from '../types/npc';
import type { SceneDocument, SceneNpcPlacement } from '../types/scene';
import {
  DEFAULT_CHARACTER_SCALE_MULTIPLIER,
  normalizeCharacterScale,
  resolveCharacterScaleWithMultiplier,
} from './avatarScale';
import { mergeWorldAnnotations, normalizeWorldAnnotations } from '../lib/worldAnnotations';
import { applyRuntimeStateToAnnotations } from '../lib/worldAnnotationRuntime';
import { DEFAULT_WORLD_LAYOUT, normalizeCameraZoom, normalizeWorldLayout } from './worldLayout';
import { SUNNYSIDE_TILESET_KEY } from './sunnysideAssets';
import {
  DEFAULT_WORLD_ID,
  WORLD_OPTIONS,
  getWorldProfile,
  type WorldProfile,
} from './worldCatalog';

const DEFAULT_SCENE_OWNER_ID = 'system';
const DEFAULT_SCENE_OWNER_DISPLAY_NAME = 'Waysprite';
const DEFAULT_WORLD_FALLBACK_BACKGROUND =
  '/assets/backgrounds/sunnyside/village-forest-lakes-final.png';

const resolveMaskPath = (scene: SceneDocument): string =>
  scene.traversalMaskUrl ??
  scene.backgroundUrl ??
  scene.thumbnailUrl ??
  DEFAULT_WORLD_FALLBACK_BACKGROUND;

const resolveBackdropPath = (scene: SceneDocument): string =>
  scene.backgroundUrl ?? scene.thumbnailUrl ?? DEFAULT_WORLD_FALLBACK_BACKGROUND;

const resolveForegroundPath = (scene: SceneDocument): string | null =>
  scene.foregroundUrl?.trim() || null;

const resolveThumbnailPath = (scene: SceneDocument): string =>
  scene.thumbnailUrl ?? scene.backgroundUrl ?? DEFAULT_WORLD_FALLBACK_BACKGROUND;

const isSceneStatus = (value: string): value is SceneDocument['status'] =>
  value === 'draft' || value === 'published' || value === 'featured' || value === 'archived';

const resolveZoneFromProfile = (worldProfile: WorldProfile, tileX: number, tileY: number): string =>
  worldProfile.zones.find(
    (zone) =>
      tileX >= zone.minTileX &&
      tileX <= zone.maxTileX &&
      tileY >= zone.minTileY &&
      tileY <= zone.maxTileY,
  )?.id ?? worldProfile.defaultZone;

const toPlacementLookupIds = (placement: SceneNpcPlacement): string[] => {
  const ids = new Set<string>();
  const sourceNpcId = (placement.sourceNpcId ?? '').trim();
  if (sourceNpcId) {
    ids.add(sourceNpcId);
  }

  const placementId = placement.placementId.trim();
  if (!placementId) {
    return [...ids];
  }

  const sourcePrefixMatch = placementId.match(/^(?:system|user|inline):(.+)$/i);
  if (sourcePrefixMatch?.[1]) {
    ids.add(sourcePrefixMatch[1].trim());
  }

  const ordinalSuffixMatch = placementId.match(/^([a-z0-9_-]+)-\d+$/i);
  if (ordinalSuffixMatch?.[1]) {
    ids.add(ordinalSuffixMatch[1].trim());
  }

  return [...ids].filter((entry) => entry.length > 0);
};

const toPlacementNpcId = (sceneId: string, placement: SceneNpcPlacement): string => {
  if (placement.source === 'user') {
    const sourceNpcId = (placement.sourceNpcId ?? '').trim();
    return sourceNpcId ? `user:${sourceNpcId}` : `scene:${sceneId}:npc:${placement.placementId}`;
  }
  if (placement.source === 'inline') {
    return `scene:${sceneId}:npc:${placement.placementId}`;
  }
  return (placement.sourceNpcId ?? '').trim() || `scene:${sceneId}:npc:${placement.placementId}`;
};

const missingNpcPlaceholder = (
  sceneId: string,
  placement: SceneNpcPlacement,
  sourceNpcId: string,
  worldLayout = DEFAULT_WORLD_LAYOUT,
): NpcConfig => {
  const movementConfig = normalizeMovementConfig(
    placement.movementConfig,
    placement.waypointPath,
    worldLayout,
  );

  return {
    id: toPlacementNpcId(sceneId, placement),
    placementId: placement.placementId,
    source: placement.source,
    sourceNpcId: sourceNpcId || undefined,
    name: '[Missing NPC]',
    title: 'Unavailable',
    expertise: '',
    personality: 'This NPC could not be loaded.',
    backstory: 'The source NPC document no longer exists or is not visible to this user.',
    greeting: 'I am unavailable right now.',
    spriteId: 'npc_user',
    spawnX: placement.spawnX,
    spawnY: placement.spawnY,
    zone: placement.zone,
    llmProvider: 'gemini',
    llmModel: DEFAULT_LLM_MODEL,
    fallbackProviders: [],
    temperature: 0.7,
    maxTokens: 500,
    movementConfig,
    waypointPath: toLegacyWaypointPath(movementConfig, placement.waypointPath),
    agentConfig: null,
  };
};

const applyPlacement = (
  sceneId: string,
  baseNpc: NpcDefinition,
  placement: SceneNpcPlacement,
  worldLayout = DEFAULT_WORLD_LAYOUT,
): NpcConfig => {
  const movementConfig = normalizeMovementConfig(
    placement.movementConfig,
    placement.waypointPath,
    worldLayout,
  );

  return {
    ...baseNpc,
    id: toPlacementNpcId(sceneId, placement),
    placementId: placement.placementId,
    source: placement.source,
    sourceNpcId: placement.sourceNpcId ?? baseNpc.sourceNpcId,
    spawnX: placement.spawnX,
    spawnY: placement.spawnY,
    zone: placement.zone,
    movementConfig,
    waypointPath: toLegacyWaypointPath(movementConfig, placement.waypointPath),
  };
};

const normalizeResolvedPlacement = (
  placement: SceneNpcPlacement,
  source: 'system' | 'user',
  resolvedSourceNpcId?: string,
): SceneNpcPlacement => {
  const normalizedSourceNpcId =
    placement.source !== source
      ? source === 'user'
        ? (resolvedSourceNpcId ?? '').replace(/^user:/, '').trim()
        : (resolvedSourceNpcId ?? '').trim()
      : source === 'user'
        ? placement.sourceNpcId
          ? placement.sourceNpcId.replace(/^user:/, '').trim()
          : (resolvedSourceNpcId ?? '').replace(/^user:/, '').trim()
        : (placement.sourceNpcId ?? resolvedSourceNpcId ?? '').trim();

  return {
    ...placement,
    source,
    sourceNpcId: normalizedSourceNpcId || undefined,
  };
};

const resolvePlacementSnapshotNpc = (
  sceneId: string,
  placement: SceneNpcPlacement,
  worldLayout = DEFAULT_WORLD_LAYOUT,
): NpcConfig => {
  const inlineConfig = placement.inlineConfig;
  if (!inlineConfig) {
    return missingNpcPlaceholder(sceneId, placement, '', worldLayout);
  }

  const movementConfig = normalizeMovementConfig(
    placement.movementConfig,
    placement.waypointPath,
    worldLayout,
  );

  return {
    id: toPlacementNpcId(sceneId, placement),
    placementId: placement.placementId,
    source: placement.source,
    sourceNpcId: placement.sourceNpcId,
    name: inlineConfig.name,
    title: inlineConfig.title,
    expertise: inlineConfig.expertise,
    personality: inlineConfig.personality,
    backstory: inlineConfig.backstory,
    greeting: inlineConfig.greeting,
    spriteId: inlineConfig.spriteId?.trim() || 'npc_user',
    avatarLayers: inlineConfig.avatarLayers,
    spriteColors: normalizeInlineSpriteColors(inlineConfig.spriteColors, {
      avatarLayers: inlineConfig.avatarLayers,
      spriteId: inlineConfig.spriteId,
    }),
    personalitySpec: inlineConfig.personalitySpec,
    spawnX: placement.spawnX,
    spawnY: placement.spawnY,
    zone: placement.zone,
    llmProvider: inlineConfig.llmProvider,
    llmModel: inlineConfig.llmModel,
    fallbackProviders: cloneFallbackProviders(inlineConfig.fallbackProviders),
    temperature: inlineConfig.temperature,
    maxTokens: inlineConfig.maxTokens,
    movementConfig,
    waypointPath: toLegacyWaypointPath(movementConfig, placement.waypointPath),
    agentConfig: inlineConfig.agentConfig ?? null,
  };
};

export const sceneDocumentToWorldProfile = (scene: SceneDocument): WorldProfile => ({
  id: scene.id,
  worldLayout: normalizeWorldLayout(scene.worldLayout),
  defaultCameraZoom: normalizeCameraZoom(scene.defaultCameraZoom),
  option: {
    id: scene.id,
    name: scene.name,
    description: scene.description,
    previewPath: resolveThumbnailPath(scene),
  },
  backdrop: {
    key: `scene-bg-${scene.id}`,
    path: resolveBackdropPath(scene),
  },
  foreground: resolveForegroundPath(scene)
    ? {
        key: `scene-fg-${scene.id}`,
        path: resolveForegroundPath(scene) ?? '',
      }
    : null,
  animatedGifs: scene.animatedGifs ?? [],
  collisionTileset: {
    key: SUNNYSIDE_TILESET_KEY,
  },
  playerSpawnTile: scene.playerSpawnTile,
  defaultZone: scene.defaultZone,
  zones: scene.zones,
  annotations: mergeWorldAnnotations({
    annotations: applyRuntimeStateToAnnotations(
      normalizeWorldAnnotations(scene.annotations, normalizeWorldLayout(scene.worldLayout)),
      scene.annotationRuntimeStateById,
    ),
    signs: scene.signs,
    doors: scene.doors,
    layout: normalizeWorldLayout(scene.worldLayout),
  }),
  ambientZoneProfile: scene.ambientZoneProfile,
  npcScatterLayout: [],
  walkableMask: {
    type: 'pixel-sample',
    sourceTexture: {
      key: `scene-mask-${scene.id}`,
      path: resolveMaskPath(scene),
    },
    isBlockedColor: (red, green, blue, alpha): boolean => {
      if (alpha < 10) {
        return true;
      }
      return red < 128 && green < 128 && blue < 128;
    },
  },
  characterScale: resolveCharacterScaleWithMultiplier(
    scene.characterScale,
    scene.characterScaleMultiplier,
  ),
});

export const resolveSceneNpcConfigs = (
  scene: SceneDocument,
  npcCatalog: NpcDefinition[],
): NpcConfig[] => {
  const worldLayout = normalizeWorldLayout(scene.worldLayout);
  const systemById = new Map<string, NpcDefinition>();
  const userByDocId = new Map<string, NpcDefinition>();

  npcCatalog.forEach((npc) => {
    if (npc.source === 'user') {
      const sourceNpcId = (npc.sourceNpcId ?? npc.id.replace(/^user:/, '')).trim();
      if (sourceNpcId) {
        userByDocId.set(sourceNpcId, npc);
      }
      return;
    }
    systemById.set(npc.id, npc);
  });
  defaultNpcs.forEach((npc) => {
    if (!systemById.has(npc.id)) {
      systemById.set(npc.id, {
        ...npc,
        source: 'system',
      });
    }
  });

  const resolveSystemNpc = (
    placement: SceneNpcPlacement,
  ): { npc: NpcDefinition; resolvedSourceNpcId?: string } | null => {
    const lookupIds = toPlacementLookupIds(placement);
    for (const lookupId of lookupIds) {
      const match = systemById.get(lookupId);
      if (match) {
        return { npc: match, resolvedSourceNpcId: lookupId };
      }
    }
    return null;
  };

  const resolveUserNpc = (
    placement: SceneNpcPlacement,
  ): { npc: NpcDefinition; resolvedSourceNpcId?: string } | null => {
    const lookupIds = toPlacementLookupIds(placement);
    for (const lookupId of lookupIds) {
      const normalizedDocId = lookupId.replace(/^user:/, '').trim();
      if (!normalizedDocId) {
        continue;
      }
      const match = userByDocId.get(normalizedDocId);
      if (match) {
        return { npc: match, resolvedSourceNpcId: normalizedDocId };
      }
    }
    return null;
  };

  return scene.npcPlacements.map((placement) => {
    if (placement.source === 'inline') {
      return resolvePlacementSnapshotNpc(scene.id, placement, worldLayout);
    }

    if (placement.source === 'system') {
      const resolvedSystemNpc = resolveSystemNpc(placement);
      if (resolvedSystemNpc) {
        return applyPlacement(
          scene.id,
          resolvedSystemNpc.npc,
          normalizeResolvedPlacement(placement, 'system', resolvedSystemNpc.resolvedSourceNpcId),
          worldLayout,
        );
      }

      const resolvedUserNpc = resolveUserNpc(placement);
      if (resolvedUserNpc) {
        return applyPlacement(
          scene.id,
          resolvedUserNpc.npc,
          normalizeResolvedPlacement(placement, 'user', resolvedUserNpc.resolvedSourceNpcId),
          worldLayout,
        );
      }

      return missingNpcPlaceholder(
        scene.id,
        placement,
        (placement.sourceNpcId ?? '').trim(),
        worldLayout,
      );
    }

    const resolvedUserNpc = resolveUserNpc(placement);
    if (resolvedUserNpc) {
      return applyPlacement(
        scene.id,
        resolvedUserNpc.npc,
        normalizeResolvedPlacement(placement, 'user', resolvedUserNpc.resolvedSourceNpcId),
        worldLayout,
      );
    }

    if (placement.inlineConfig) {
      return resolvePlacementSnapshotNpc(scene.id, placement, worldLayout);
    }

    const resolvedSystemNpc = resolveSystemNpc(placement);
    if (resolvedSystemNpc) {
      return applyPlacement(
        scene.id,
        resolvedSystemNpc.npc,
        normalizeResolvedPlacement(placement, 'system', resolvedSystemNpc.resolvedSourceNpcId),
        worldLayout,
      );
    }

    return missingNpcPlaceholder(
      scene.id,
      placement,
      (placement.sourceNpcId ?? '').trim(),
      worldLayout,
    );
  });
};

export const createFallbackSceneDocumentFromWorldProfile = (
  worldProfile: WorldProfile,
): SceneDocument => {
  const option = worldProfile.option;
  const placements = defaultNpcs.map((npc, index) => {
    const layout = worldProfile.npcScatterLayout[index % worldProfile.npcScatterLayout.length];
    const spawnX =
      layout?.spawnX ?? Math.min(2 + index * 2, worldProfile.worldLayout.tileWidth - 1);
    const spawnY =
      layout?.spawnY ?? Math.min(2 + index * 2, worldProfile.worldLayout.tileHeight - 1);
    const movementConfig = layout?.waypointPath?.length
      ? movementConfigFromWaypointPath(layout.waypointPath)
      : cloneMovementConfig(DEFAULT_MOVEMENT_CONFIG, worldProfile.worldLayout);

    return {
      placementId: `${npc.id}-${index + 1}`,
      source: 'system' as const,
      sourceNpcId: npc.id,
      spawnX,
      spawnY,
      zone: resolveZoneFromProfile(worldProfile, spawnX, spawnY),
      movementConfig,
      waypointPath: toLegacyWaypointPath(movementConfig, layout?.waypointPath),
    };
  });

  return {
    id: worldProfile.id,
    name: option.name,
    description: option.description,
    tags: ['system'],
    ownerId: DEFAULT_SCENE_OWNER_ID,
    ownerDisplayName: DEFAULT_SCENE_OWNER_DISPLAY_NAME,
    status: 'featured',
    levelDesignId: `seed-${worldProfile.id}`,
    backgroundUrl: worldProfile.backdrop.path,
    foregroundUrl: worldProfile.foreground?.path ?? null,
    traversalMaskUrl:
      worldProfile.walkableMask.type === 'pixel-sample'
        ? (worldProfile.walkableMask.sourceTexture?.path ?? null)
        : null,
    thumbnailUrl: option.previewPath,
    animatedGifs: worldProfile.animatedGifs ?? [],
    worldLayout: worldProfile.worldLayout,
    defaultCameraZoom: worldProfile.defaultCameraZoom,
    playerSpawnTile: worldProfile.playerSpawnTile,
    defaultZone: worldProfile.defaultZone,
    zones: worldProfile.zones,
    signs: [],
    doors: [],
    annotations: normalizeWorldAnnotations(worldProfile.annotations, worldProfile.worldLayout),
    annotationRuntimeStateById: undefined,
    npcPlacements: placements,
    characterScale: normalizeCharacterScale(worldProfile.characterScale),
    characterScaleMultiplier: DEFAULT_CHARACTER_SCALE_MULTIPLIER,
    ambientZoneProfile: worldProfile.ambientZoneProfile,
    visitCount: 0,
    createdAt: null,
    updatedAt: null,
    publishedAt: null,
  };
};

export const createFallbackSceneDocuments = (): SceneDocument[] => {
  const fallbackById = new Map<string, SceneDocument>();

  WORLD_OPTIONS.forEach((option) => {
    const profile = getWorldProfile(option.id);
    fallbackById.set(option.id, createFallbackSceneDocumentFromWorldProfile(profile));
  });

  if (!fallbackById.has(DEFAULT_WORLD_ID)) {
    const profile = getWorldProfile(DEFAULT_WORLD_ID);
    fallbackById.set(DEFAULT_WORLD_ID, createFallbackSceneDocumentFromWorldProfile(profile));
  }

  return [...fallbackById.values()];
};

export const normalizeSceneStatus = (
  value: unknown,
  fallback: SceneDocument['status'],
): SceneDocument['status'] =>
  isSceneStatus(String(value)) ? (String(value) as SceneDocument['status']) : fallback;
